import axios from 'axios';

import { createApiClient } from '@/api/http-client';
import { handleSessionExpired } from '@/api/session-expiry';
import { config } from '@/config';
import type { EscalationHistoryResponseDTO } from '@/models/escalation.model';
import { createLogger } from '@/utils/logging';

import { ProjectHomePageData } from './mocks/projectHome.types';

const logger = createLogger('ProjectDetailsAPI', 'api');

/** Typed error thrown by project details API functions. */
export class ProjectDetailsApiError extends Error {
  public readonly statusCode: number | undefined;

  constructor(message: string, statusCode?: number) {
    super(message);
    this.name = 'ProjectDetailsApiError';
    this.statusCode = statusCode;
  }
}

const projectDetailsApiClient = createApiClient({
  baseURL: config.apiUrl,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});
void projectDetailsApiClient;

const parseProjectDetailsApiError = (error: unknown): ProjectDetailsApiError => {
  handleSessionExpired(error);
  if (!axios.isAxiosError(error)) {
    const message = error instanceof Error ? error.message : 'Request failed';
    return new ProjectDetailsApiError(message);
  }

  const statusCode = error.response?.status;
  const data = error.response?.data as Record<string, unknown> | undefined;
  const detail = data?.detail;

  if (typeof detail === 'string' && detail.trim().length > 0) {
    return new ProjectDetailsApiError(detail.trim(), statusCode);
  }
  return new ProjectDetailsApiError(error.message, statusCode);
};

/**
 * Retrieves a single project by ID with selective data loading.
 * GET /api/v1/clients/:clientId/projects/:projectId?include=metadata,team,health,trends,audit,updates
 *
 * Query parameter 'include' controls what data is returned:
 * - metadata: client name, project name, dates, budget
 * - team: full team roster with hours and allocation
 * - health: latest health snapshot
 * - trends: 12-week health history
 * - audit: audit log entries
 * - updates: weekly status updates history
 *
 * Without include: returns basic project record (ProjectWorkflowResponse)
 * With include: returns comprehensive dashboard data (ProjectHomePageResponseDTO)
 */
export const getProjectDetails = async (
  accessToken: string,
  clientId: string,
  projectId: string,
  include: string = 'metadata,team,health,trends,audit,updates',
): Promise<ProjectHomePageData> => {
  try {
    const response = await projectDetailsApiClient.get<ProjectHomePageData>(
      `/clients/${clientId}/projects/${projectId}`,
      {
        params: { include },
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data;
  } catch (error) {
    const parsedError = parseProjectDetailsApiError(error);
    logger.error(`Failed to get project details for ${projectId}`, parsedError);
    throw parsedError;
  }
};

/** Request body for adding a project link */
export interface AddLinkRequest {
  label: string;
  url: string;
}

/** Response when adding a project link */
export interface LinkResponse {
  id: string;
  label: string;
  url: string;
  createdAt?: string;
}

/**
 * Add a project link (Teams, GitHub, SharePoint, etc.)
 * POST /api/v1/clients/:clientId/projects/:projectId/links
 */
export const addProjectLink = async (
  accessToken: string,
  clientId: string,
  projectId: string,
  link: AddLinkRequest,
): Promise<LinkResponse> => {
  try {
    const response = await projectDetailsApiClient.post<LinkResponse>(
      `/clients/${clientId}/projects/${projectId}/links`,
      link,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    logger.info(`Added link to project ${projectId}`);
    return response.data;
  } catch (error) {
    const parsedError = parseProjectDetailsApiError(error);
    logger.error(`Failed to add link to project ${projectId}`, parsedError);
    throw parsedError;
  }
};

/** Request body for adding a tech stack item */
export interface AddTechStackRequest {
  name: string;
  category: string;
}

/** Response when adding a tech stack item */
export interface TechStackResponse {
  id: string;
  name: string;
  category: string;
  createdAt?: string;
}

/**
 * Add a technology to the project tech stack
 * POST /api/v1/clients/:clientId/projects/:projectId/tech-stack
 */
export const addTechStackItem = async (
  accessToken: string,
  clientId: string,
  projectId: string,
  techStack: AddTechStackRequest,
): Promise<TechStackResponse> => {
  try {
    const response = await projectDetailsApiClient.post<TechStackResponse>(
      `/clients/${clientId}/projects/${projectId}/tech-stack`,
      techStack,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    logger.info(`Added tech stack item to project ${projectId}`);
    return response.data;
  } catch (error) {
    const parsedError = parseProjectDetailsApiError(error);
    logger.error(`Failed to add tech stack item to project ${projectId}`, parsedError);
    throw parsedError;
  }
};

/** Request body for adding a weekly update */
export interface AddWeeklyUpdateRequest {
  week_of: string;
  on_schedule: { status: string; notes?: string };
  on_budget: { status: string; notes: string };
  csat: { status: string; notes?: string };
  team_health: { status: string; notes?: string };
  notes?: string;
  accomplishments: string;
  plan_next_week: string;
  risk_blockers: string;
  milestone_snapshot?: Array<{ title: string; status: string; dueDate: string }>;
}

/** Response when adding a weekly update */
export interface WeeklyUpdateResponse {
  id: string;
  weekOf: string;
  createdBy: string;
  createdAt: string;
  onSchedule: { status: string; notes?: string };
  onBudget?: { status: string; notes?: string } | null;
  csat: { status: string; notes?: string };
  teamHealth: { status: string; notes?: string };
  accomplishments: string;
  planNextWeek: string;
  riskBlockers: string;
  milestoneSnapshot?: Array<{ title: string; status: string; dueDate: string }>;
}

export interface ProjectWeeklyStatusPermissions {
  can_create_weekly_status: boolean;
  can_update_weekly_status: boolean;
  can_view_weekly_status: boolean;
}

/** Retrieve backend-derived weekly-status permissions for one project. */
export const getProjectWeeklyStatusPermissions = async (
  accessToken: string,
  clientId: string,
  projectId: string,
): Promise<ProjectWeeklyStatusPermissions> => {
  try {
    const response = await projectDetailsApiClient.get<ProjectWeeklyStatusPermissions>(
      `/clients/${clientId}/projects/${projectId}/permissions`,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    return response.data;
  } catch (error) {
    const parsedError = parseProjectDetailsApiError(error);
    logger.error(`Failed to get weekly-status permissions for ${projectId}`, parsedError);
    throw parsedError;
  }
};

/**
 * Add a weekly status update to the project
 * POST /api/v1/clients/:clientId/projects/:projectId/weekly-updates
 */
export const addWeeklyUpdate = async (
  accessToken: string,
  clientId: string,
  projectId: string,
  update: AddWeeklyUpdateRequest,
): Promise<WeeklyUpdateResponse> => {
  try {
    const response = await projectDetailsApiClient.post<WeeklyUpdateResponse>(
      `/clients/${clientId}/projects/${projectId}/weekly-updates`,
      update,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    logger.info(`Added weekly update to project ${projectId}`);
    return response.data;
  } catch (error) {
    const parsedError = parseProjectDetailsApiError(error);
    logger.error(`Failed to add weekly update to project ${projectId}`, parsedError);
    throw parsedError;
  }
};

/**
 * Update an existing weekly status update
 * PUT /api/v1/clients/:clientId/projects/:projectId/weekly-updates/:weekOf
 */
export const updateWeeklyUpdate = async (
  accessToken: string,
  clientId: string,
  projectId: string,
  weekOf: string,
  update: AddWeeklyUpdateRequest,
): Promise<WeeklyUpdateResponse> => {
  try {
    const response = await projectDetailsApiClient.put<WeeklyUpdateResponse>(
      `/clients/${clientId}/projects/${projectId}/weekly-updates/${weekOf}`,
      update,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    logger.info(`Updated weekly update for week ${weekOf} in project ${projectId}`);
    return response.data;
  } catch (error) {
    const parsedError = parseProjectDetailsApiError(error);
    logger.error(`Failed to update weekly update for week ${weekOf}`, parsedError);
    throw parsedError;
  }
};

/**
 * Delete a project link
 * DELETE /api/v1/clients/:clientId/projects/:projectId/links/:linkId
 */
export const deleteProjectLink = async (
  accessToken: string,
  clientId: string,
  projectId: string,
  linkId: string,
): Promise<void> => {
  try {
    await projectDetailsApiClient.delete(
      `/clients/${clientId}/projects/${projectId}/links/${linkId}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    logger.info(`Deleted project link ${linkId} from project ${projectId}`);
  } catch (error) {
    const parsedError = parseProjectDetailsApiError(error);
    logger.error(`Failed to delete project link ${linkId}`, parsedError);
    throw parsedError;
  }
};

/**
 * Delete a tech stack item
 * DELETE /api/v1/clients/:clientId/projects/:projectId/tech-stack/:techId
 */
export const deleteTechStackItem = async (
  accessToken: string,
  clientId: string,
  projectId: string,
  techId: string,
): Promise<void> => {
  try {
    await projectDetailsApiClient.delete(
      `/clients/${clientId}/projects/${projectId}/tech-stack/${techId}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    logger.info(`Deleted tech stack item ${techId} from project ${projectId}`);
  } catch (error) {
    const parsedError = parseProjectDetailsApiError(error);
    logger.error(`Failed to delete tech stack item ${techId}`, parsedError);
    throw parsedError;
  }
};

/**
 * Fetch escalation notification history for the current weekly-status cycle.
 * GET /api/v1/clients/:clientId/projects/:projectId/escalation-history
 *
 * Requires can_create_weekly_status = true; returns 403 otherwise.
 */
export const fetchEscalationHistory = async (
  accessToken: string,
  clientId: string,
  projectId: string,
): Promise<EscalationHistoryResponseDTO> => {
  try {
    const response = await projectDetailsApiClient.get<EscalationHistoryResponseDTO>(
      `/clients/${clientId}/projects/${projectId}/escalation-history`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data;
  } catch (error) {
    const parsedError = parseProjectDetailsApiError(error);
    logger.error(`Failed to fetch escalation history for project ${projectId}`, parsedError);
    throw parsedError;
  }
};

export interface WeeklyStatusAssignmentResponse {
  assigned_week: string;
  assignment_reason: string;
  is_early_submission: boolean;
  previous_week_status_exists: boolean;
  day_of_week_name: string;
  current_week: string;
  previous_week: string;
}

/**
 * Get weekly status assignment preview for the current submission day.
 * Determines which week a status update will be assigned to.
 * GET /api/v1/clients/:clientId/projects/:projectId/weekly-updates/week-assignment-preview
 */
export const getWeeklyStatusAssignment = async (
  accessToken: string,
  clientId: string,
  projectId: string,
): Promise<WeeklyStatusAssignmentResponse> => {
  try {
    const response = await projectDetailsApiClient.get<WeeklyStatusAssignmentResponse>(
      `/clients/${clientId}/projects/${projectId}/weekly-updates/week-assignment-preview`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data;
  } catch (error) {
    const parsedError = parseProjectDetailsApiError(error);
    logger.error(`Failed to get weekly status assignment for project ${projectId}`, parsedError);
    throw parsedError;
  }
};
